import { styled } from '@linaria/react';
import { Group, Modal } from '@mantine/core';
import { Button } from '../../../ui-library/button/button';
import { H3, Text } from '../../../ui-library/typography/typography';
import type { GoogleFileType, SheetAccessModalStatus } from '../hooks/use-sheet-access-gate';
import { GOOGLE_FILE_TYPE_CONFIG } from './google-file-type.config';
import { Hint, IntegrationsLink, INTEGRATIONS_PAGE_PATH } from './shared/sheet-access-hint';

interface GrantSheetAccessModalProps {
  opened: boolean;
  status: SheetAccessModalStatus;
  fileType?: GoogleFileType;
  onGrantAccess: () => void;
  onSendAnyway: () => void;
  onCancel: () => void;
  onConnectGoogle: () => void;
}

/**
 * Prompt shown when a pasted Google file (Sheet, Doc, or Slides) can't be used by Flora.
 * When the user's Google account isn't connected it prompts them to connect it;
 * once connected but the file is inaccessible it offers to grant access via the
 * Google Picker. Either way the user can send the message anyway or cancel.
 *
 * @param {GrantSheetAccessModalProps} props - Open state, status, file type, and callbacks.
 * @returns {JSX.Element} The controlled grant-access modal.
 */
export function GrantSheetAccessModal({
  opened,
  status,
  fileType = 'spreadsheet',
  onGrantAccess,
  onSendAnyway,
  onCancel,
  onConnectGoogle,
}: GrantSheetAccessModalProps) {
  const isOpeningPicker = status === 'opening-picker';
  const isConnectRequired = status === 'connect-required';
  // Nouns for the pasted file, worded for the file type in play.
  const { noun: fileNoun, label: productNoun, collectionNoun } = GOOGLE_FILE_TYPE_CONFIG[fileType];

  return (
    <Modal
      opened={opened}
      onClose={onCancel}
      centered
      radius="lg"
      size="lg"
      padding="40px"
      title={<H3>{isConnectRequired ? 'Connect your Google account?' : `Give Flora access to this ${fileNoun}?`}</H3>}
      closeButtonProps={{ 'aria-label': 'Close' }}
    >
      <Content>
        {isConnectRequired ? (
          <Text>
            Flora isn't connected to your Google account yet, so it can't open that {fileNoun}. Connect it to let Flora
            read and update your {collectionNoun}.
          </Text>
        ) : (
          <Text>
            Flora can't access that {productNoun} yet. Grant access by picking the file in Google's file picker — Flora
            can then read and update it.
          </Text>
        )}

        {status === 'picked-different' && (
          <Hint>
            <Text size="small" weight="medium">
              That's a different file than the link you pasted — pick the linked {fileNoun}, or send anyway.
            </Text>
          </Hint>
        )}

        {status === 'reconnect-required' && (
          <Hint>
            <Text size="small" weight="medium">
              Your Google connection needs to be refreshed.{' '}
              <IntegrationsLink to={INTEGRATIONS_PAGE_PATH}>Manage integrations</IntegrationsLink>
            </Text>
          </Hint>
        )}

        {status === 'error' && (
          <Hint>
            <Text size="small" weight="medium">
              Something went wrong opening the file picker. You can try again or send anyway.
            </Text>
          </Hint>
        )}

        <Group justify="flex-end" style={{ marginTop: 40 }}>
          <Button variant="subtle" onClick={onSendAnyway} radius="xl">
            <Text weight="medium">Send anyway</Text>
          </Button>
          {isConnectRequired ? (
            <Button variant="primary" onClick={onConnectGoogle} radius="xl">
              <Text weight="medium">Connect Google account</Text>
            </Button>
          ) : (
            <Button variant="primary" onClick={onGrantAccess} loading={isOpeningPicker} radius="xl">
              <Text weight="medium">Grant access</Text>
            </Button>
          )}
        </Group>
      </Content>
    </Modal>
  );
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
